'use client';

import { Check, Loader2 } from 'lucide-react';
import { Switch as SwitchPrimitive } from 'radix-ui';
import * as React from 'react';

import { cn } from '@/_shared/lib/utils';

// Track / thumb / check dimensions per size. Thumb travel = track width − 2·padding − thumb width.
const TRACK = { md: 'h-4 w-6', lg: 'h-5 w-8' } as const;
const THUMB = {
  md: 'size-3 data-[state=checked]:translate-x-2',
  lg: 'size-4 data-[state=checked]:translate-x-3',
} as const;
const CHECK = { md: 'size-2', lg: 'size-2.5' } as const;
const SPINNER = { md: 'size-2', lg: 'size-3' } as const;

function Switch({
  className,
  size = 'md',
  loading = false,
  disabled,
  ...props
}: React.ComponentProps<typeof SwitchPrimitive.Root> & {
  /** Track size — `md` = 24×16, `lg` = 32×20. */
  size?: 'md' | 'lg';
  /** Show a spinner in the thumb and block interaction while an async toggle is in flight. */
  loading?: boolean;
}) {
  return (
    <SwitchPrimitive.Root
      data-slot="switch"
      data-size={size}
      // Loading blocks interaction like `disabled`, but stays full-opacity (busy ≠ unavailable).
      disabled={loading || disabled}
      aria-busy={loading || undefined}
      className={cn(
        'group/switch peer inline-flex shrink-0 cursor-pointer items-center rounded-full p-0.5 focus-ring transition-[background-color,box-shadow] outline-none',
        TRACK[size],
        'bg-grey-200 hover:bg-grey-300',
        'data-[state=checked]:bg-primary data-[state=checked]:hover:shadow-primary',
        // When loading, show a busy cursor — NOT `disabled:cursor-not-allowed`, which (being a
        // `:disabled` rule) outranks a plain `cursor-progress` and would otherwise win, since loading
        // also sets `disabled`. Scope the not-allowed/opacity to the genuinely-disabled case.
        loading ? 'cursor-progress' : 'disabled:cursor-not-allowed disabled:opacity-50',
        className,
      )}
      {...props}
    >
      <SwitchPrimitive.Thumb
        data-slot="switch-thumb"
        className={cn(
          'pointer-events-none flex items-center justify-center rounded-full bg-grey-0 transition-transform',
          THUMB[size],
        )}
      >
        {loading ? (
          <Loader2 aria-hidden className={cn('animate-spin text-grey-400', SPINNER[size])} />
        ) : (
          /* Brand check appears inside the thumb once the switch is on. */
          <Check
            aria-hidden
            strokeWidth={3.5}
            className={cn(
              'text-primary opacity-0 transition-opacity group-data-[state=checked]/switch:opacity-100',
              CHECK[size],
            )}
          />
        )}
      </SwitchPrimitive.Thumb>
    </SwitchPrimitive.Root>
  );
}

export { Switch };
